import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import type { AdminKitchenDto, AdminUserDto, CreateAdminKitchenInput, MeDto, Role } from '@tiffinify/contracts';
import { formatDay, formatShort } from '@tiffinify/core';
import { Avatar, Badge, Banner, BtnPrimary, BtnSecondary, Card, CardTitle, Chip, Chips, Empty, ErrorText, Field, Helper, Hero, IconBtn, KV, Mono, PageLoader, PageTitle, PillAction, SearchField, Section, Segmented, Skel, TaskTitle, TopBar, brandTitle, useConfirm, LoadError } from '../components/ui';
import { ApiClientError, del, post, isNotFound } from '../lib/api';
import { errorMessage, formatInr, mealsLabel } from '../lib/format';
import { useAdminKitchenDetail, useAdminKitchens, useAdminOverview, useAdminUsers, useEnterKitchen, useMe, useVersion } from '../lib/queries';

/** Approve or reject a kitchen that signed up; the owner is emailed by the server. */
function useDecide() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; decision: 'approve' | 'reject' }) => post<{ status: string; emailed: 'sent' | 'skipped' | 'failed' }>(`/admin/kitchens/${v.id}/${v.decision}`),
    onSuccess: () => qc.invalidateQueries(),
  });
}

const emailedNote = (e: 'sent' | 'skipped' | 'failed') => (e === 'sent' ? 'The owner was emailed.' : e === 'failed' ? 'The email to the owner could not be sent.' : 'No email was sent.');

/** Approve / Reject, side by side. The kitchen usually leaves the list right after, so the parent shows the result. */
function DecideButtons({ id, name, status, onDone }: { id: string; name: string; status: AdminKitchenDto['status']; onDone: (note: string) => void }) {
  const decide = useDecide();
  const busy = decide.isPending ? decide.variables?.decision : undefined;
  const go = (decision: 'approve' | 'reject') =>
    decide.mutate({ id, decision }, { onSuccess: (r) => onDone(`${name} ${r.status === 'approved' ? 'approved' : 'rejected'}. ${emailedNote(r.emailed)}`) });
  return (
    <div className="stack">
      <div className="two">
        <BtnSecondary sm ink busy={busy === 'approve'} label="Approve" icon="check" disabled={decide.isPending} onClick={() => go('approve')} />
        {status !== 'rejected' && <BtnSecondary sm danger busy={busy === 'reject'} label="Reject" icon="close" disabled={decide.isPending} onClick={() => go('reject')} />}
      </div>
      <ErrorText>{decide.error ? errorMessage(decide.error) : ''}</ErrorText>
    </div>
  );
}

/** Kitchens that signed up and are waiting for the team, newest first, with the decision right there. */
function WaitingKitchens() {
  const list = useAdminKitchens();
  const [note, setNote] = useState('');
  const waiting = (list.data ?? []).filter((k) => k.status === 'pending');
  const done = note ? <Banner>{note}</Banner> : null;
  if (waiting.length === 0) return done;
  return (
    <Section title="Waiting for approval" meta={<Mono>{waiting.length} {waiting.length === 1 ? 'kitchen' : 'kitchens'}</Mono>}>
      {done}
      <Card>
        {waiting.map((k) => (
          <div className="approve-row" key={k.id}>
            <Link to={`/admin/k/${k.id}`} className="who">
              <b>{k.name}</b>
              <small>{[k.owner?.name, k.owner?.email].filter(Boolean).join(' · ') || 'No owner'} · signed up {joined(k.createdAt)}</small>
            </Link>
            <DecideButtons id={k.id} name={k.name} status={k.status} onDone={setNote} />
          </div>
        ))}
      </Card>
    </Section>
  );
}

/*
 * The Tiffinify team's pages: every kitchen and person on Tiffinify, read-only, plus opening a kitchen to help
 * (as visiting staff, see sessionUser in the Worker) and adding a kitchen for a new owner.
 */

type Tab = 'overview' | 'kitchens' | 'people';
const TABS: Array<{ value: Tab; label: string }> = [
  { value: 'overview', label: 'Overview' },
  { value: 'kitchens', label: 'Kitchens' },
  { value: 'people', label: 'People' },
];

const roleLabel = (r: Role) => (r === 'owner' ? 'Owner' : 'Staff');
const joined = (iso: string) => formatShort(iso.slice(0, 10));
const matches = (q: string, ...words: Array<string | null | undefined>) => {
  const needle = q.trim().toLowerCase();
  return !needle || words.some((w) => w?.toLowerCase().includes(needle));
};

/** Opens a kitchen to help in it: the whole app switches to that kitchen until you go back. */
function useVisit() {
  const enter = useEnterKitchen();
  return useMutation({ mutationFn: (kitchenId: string) => post<MeDto>('/admin/switch', { kitchenId }), onSuccess: enter });
}

export function Admin() {
  const [params, setParams] = useSearchParams();
  const tab = (TABS.find((t) => t.value === params.get('tab'))?.value ?? 'overview') as Tab;
  const overview = useAdminOverview();
  const o = overview.data;

  return (
    <main className="main task">
      <TopBar to="/settings" label="Back to settings" logo={false} />
      <PageTitle
        title={
          <span className="team-title">{brandTitle('tiffinify')}<span className="team-pill">team</span></span>
        }
        sub={o ? `${o.totalKitchens} kitchens · ${o.totalUsers} people` : <Skel w={160} />}
        action={tab === 'kitchens' ? <PillAction to="/admin/new" label="Add" icon="plus" /> : undefined}
      />
      <Segmented label="Show" value={tab} onChange={(t) => setParams(t === 'overview' ? {} : { tab: t }, { replace: true })} options={TABS} />
      {tab === 'overview' && <><WaitingKitchens /><Overview /></>}
      {tab === 'kitchens' && <Kitchens />}
      {tab === 'people' && <People />}
    </main>
  );
}

function Overview() {
  const q = useAdminOverview();
  const o = q.data;
  if (q.isPending) return <PageLoader label="Counting kitchens" />;
  if (!o) return <LoadError what="the overview" onRetry={() => void q.refetch()} />;
  const marked = o.todayDeliveries;

  return (
    <>
      <Hero label="Tiffinify today">
        <Mono muted>All kitchens · today</Mono>
        <div className="team-stats">
          <span><b>{o.totalKitchens}</b>kitchens</span>
          <span><b>{o.totalActiveSubscriptions}</b>running plans</span>
          <span><b>{o.totalSubscribers}</b>customers</span>
          <span><b>{marked.delivered}</b>delivered today</span>
        </div>
        <p>
          <strong>{formatInr(o.totalRevenue)}</strong> recorded in payments so far
          {marked.total > 0 && <> · {marked.notHome} not home, {marked.failed} couldn’t deliver today</>}.
        </p>
      </Hero>

      <Section title="Newest kitchens" meta={<Link to="/admin?tab=kitchens" className="text-link">All</Link>}>
        {o.recentKitchens.length === 0 ? (
          <Card><Empty title="No kitchens yet" /></Card>
        ) : (
          <Card>
            {o.recentKitchens.map((k) => (
              <Link key={k.id} to={`/admin/k/${k.id}`} className="sub-row">
                <Avatar name={k.name} />
                <span className="mid">
                  <span className="name"><b>{k.name}</b></span>
                  <span className="meta">{[k.ownerName, k.ownerEmail].filter(Boolean).join(' · ') || 'No owner'}</span>
                </span>
                <span className="end"><span><b>{k.activeSubscribers}</b> active</span><small>{joined(k.createdAt)}</small></span>
              </Link>
            ))}
          </Card>
        )}
      </Section>

      <Section title="Newest people" meta={<Link to="/admin?tab=people" className="text-link">All</Link>}>
        {o.recentUsers.length === 0 ? (
          <Card><Empty title="No one yet" /></Card>
        ) : (
          <Card pad>
            {o.recentUsers.map((u) => (
              <div className="list-row" key={u.id}>
                <span className="grow">
                  <b>{u.name}</b>
                  <small className="ellipsis">{u.email ?? u.phone ?? '—'}</small>
                </span>
                <small>{u.kitchenCount === 1 ? '1 kitchen' : `${u.kitchenCount} kitchens`}</small>
              </div>
            ))}
          </Card>
        )}
      </Section>
    </>
  );
}

type KitchenFilter = 'all' | 'waiting' | 'active' | 'empty';
const KITCHEN_FILTERS: Array<{ value: KitchenFilter; label: string; test: (k: AdminKitchenDto) => boolean }> = [
  { value: 'all', label: 'All', test: () => true },
  { value: 'waiting', label: 'Waiting', test: (k) => k.status === 'pending' },
  { value: 'active', label: 'With customers', test: (k) => k.activeSubscriberCount > 0 },
  { value: 'empty', label: 'Not started', test: (k) => k.subscriberCount === 0 },
];

function Kitchens() {
  const list = useAdminKitchens();
  const version = useVersion().data;
  const qc = useQueryClient();
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<KitchenFilter>('all');
  const sample = useMutation({
    mutationFn: () => post<{ kitchenId: string }>('/admin/seed-sample'),
    onSuccess: async (r) => { await qc.invalidateQueries(); nav(`/admin/k/${r.kitchenId}`); },
  });

  const all = list.data ?? [];
  const shown = all.filter((k) => KITCHEN_FILTERS.find((f) => f.value === filter)!.test(k) && matches(q, k.name, k.owner?.name, k.owner?.email));

  return (
    <>
      <SearchField value={q} onChange={setQ} label="Search kitchens" placeholder="Kitchen, owner or email" />
      <Chips label="Filter kitchens" scroll>
        {KITCHEN_FILTERS.map((f) => (
          <Chip key={f.value} on={filter === f.value} count={list.data ? all.filter(f.test).length : null} onClick={() => setFilter(f.value)}>{f.label}</Chip>
        ))}
      </Chips>
      {list.isPending ? (
        <PageLoader label="Finding kitchens" />
      ) : shown.length === 0 ? (
        <Card><Empty title={q || filter !== 'all' ? 'No kitchen matches' : 'No kitchens yet'}>{list.error ? errorMessage(list.error) : null}</Empty></Card>
      ) : (
        <Card>{shown.map((k) => <KitchenRow key={k.id} k={k} />)}</Card>
      )}
      {version && version.env !== 'production' && (
        <div className="stack">
          <Helper icon="info">Test servers only: a kitchen with three made-up customers, owned by you.</Helper>
          <BtnSecondary sm busy={sample.isPending} label="Add a sample kitchen" icon="plus" onClick={() => sample.mutate()} />
          <ErrorText>{sample.error ? errorMessage(sample.error) : ''}</ErrorText>
        </div>
      )}
    </>
  );
}

function KitchenRow({ k }: { k: AdminKitchenDto }) {
  return (
    <Link to={`/admin/k/${k.id}`} className="sub-row">
      <Avatar name={k.name} />
      <span className="mid">
        <span className="name"><b>{k.name}</b>{k.status === 'pending' ? <Badge kind="due">Waiting</Badge> : k.status === 'rejected' ? <Badge kind="end">Rejected</Badge> : null}</span>
        <span className="meta">{k.owner?.name ?? 'No owner'} · {mealsLabel(k.meals)}</span>
      </span>
      <span className="end">
        <span><b>{k.activeSubscriberCount}</b> active</span>
        <small>{formatInr(k.revenueThisMonth)} this month</small>
      </span>
    </Link>
  );
}

type PeopleFilter = 'all' | 'owner' | 'staff' | 'none';
const PEOPLE_FILTERS: Array<{ value: PeopleFilter; label: string; test: (u: AdminUserDto) => boolean }> = [
  { value: 'all', label: 'All', test: () => true },
  { value: 'owner', label: 'Owners', test: (u) => u.kitchens.some((k) => k.role === 'owner') },
  { value: 'staff', label: 'Staff', test: (u) => u.kitchens.some((k) => k.role === 'staff') },
  { value: 'none', label: 'No kitchen', test: (u) => u.kitchens.length === 0 },
];

function People() {
  const { confirm, sheet: confirmSheet } = useConfirm();
  const list = useAdminUsers();
  const me = useMe().data;
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<PeopleFilter>('all');
  const all = list.data ?? [];
  const shown = all.filter((u) => PEOPLE_FILTERS.find((f) => f.value === filter)!.test(u) && matches(q, u.name, u.email, u.phone, ...u.kitchens.map((k) => k.kitchenName)));

  const delUser = useMutation({
    mutationFn: (userId: string) => del<{ ok: boolean }>(`/admin/users/${userId}`),
    onSuccess: () => void qc.invalidateQueries(),
  });

  return (
    <>
      {confirmSheet}
      <SearchField value={q} onChange={setQ} label="Search people" placeholder="Name, email or kitchen" />
      <Chips label="Filter people" scroll>
        {PEOPLE_FILTERS.map((f) => (
          <Chip key={f.value} on={filter === f.value} count={list.data ? all.filter(f.test).length : null} onClick={() => setFilter(f.value)}>{f.label}</Chip>
        ))}
      </Chips>
      {list.isPending ? (
        <PageLoader label="Finding people" />
      ) : shown.length === 0 ? (
        <Card><Empty title={q || filter !== 'all' ? 'No one matches' : 'No one yet'}>{list.error ? errorMessage(list.error) : null}</Empty></Card>
      ) : (
        <Card pad>
          {shown.map((u) => (
            <div className="list-row person-row" key={u.id}>
              <Avatar name={u.name} />
              <span className="grow">
                <b>{u.name}</b>
                <small className="ellipsis">{[u.email ?? u.phone, u.hasGoogle ? 'Google' : null].filter(Boolean).join(' · ')}</small>
                <small>
                  {u.kitchens.length === 0
                    ? 'No kitchen'
                    : u.kitchens.map((k, i) => (
                        <span key={k.kitchenId}>{i > 0 && ', '}<Link to={`/admin/k/${k.kitchenId}`} className="inline-link">{k.kitchenName}</Link> ({roleLabel(k.role)})</span>
                      ))}
                </small>
              </span>
              <span className="person-row-end">
                <small>{joined(u.createdAt)}</small>
                {u.id !== me?.user.id && (
                  <IconBtn
                    icon="trash"
                    label={`Delete ${u.name}`}
                    ghost
                    disabled={delUser.isPending && delUser.variables === u.id}
                    onClick={async () => {
                      const where = u.kitchens.length ? ` They leave ${u.kitchens.map((k) => k.kitchenName).join(', ')}.` : '';
                      if (await confirm({ title: `Delete ${u.name}?`, body: `${u.email || u.phone || 'No email'} is deleted for good.${where}`, yes: 'Delete person', danger: true })) delUser.mutate(u.id);
                    }}
                  />
                )}
              </span>
            </div>
          ))}
        </Card>
      )}
      <ErrorText>{delUser.error ? errorMessage(delUser.error) : ''}</ErrorText>
    </>
  );
}

/** One kitchen, as the Tiffinify team sees it. */
export function AdminKitchen() {
  const { confirm, sheet: confirmSheet } = useConfirm();
  const { id = '' } = useParams();
  const q = useAdminKitchenDetail(id);
  const d = q.data;
  const visit = useVisit();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [note, setNote] = useState('');
  const back = <TopBar to="/admin?tab=kitchens" label="Back to kitchens" logo={false} />;

  const delKitchen = useMutation({
    mutationFn: () => del<{ ok: boolean }>(`/admin/kitchens/${id}`),
    onSuccess: async () => {
      await qc.invalidateQueries();
      nav('/admin?tab=kitchens', { replace: true });
    },
  });

  const delSub = useMutation({
    mutationFn: (subId: string) => del<{ ok: boolean }>(`/admin/kitchens/${id}/subscribers/${subId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-kitchen-detail', id] });
      void qc.invalidateQueries({ queryKey: ['admin-overview'] });
    },
  });

  if (q.isPending) return <main className="main task">{back}<div className="one-line"><TaskTitle title="Kitchen" sub={<Skel w={160} />} /></div><PageLoader label="Opening the kitchen" /></main>;
  if (q.isError && !isNotFound(q.error)) return <main className="main task">{back}<LoadError what="this kitchen" onRetry={() => void q.refetch()} /></main>;
  if (!d) return <main className="main task">{back}<Card><Empty title="Kitchen not found" /></Card></main>;
  const k = d.kitchen;

  return (
    <main className="main task">
      {confirmSheet}
      {back}
      {/* One line, so a long name does not push the page down as it loads; the full name is in the tooltip. */}
      <div className="one-line" title={k.name}><TaskTitle title={k.name} sub={`Since ${formatDay(k.createdAt.slice(0, 10))} · ${mealsLabel(k.meals)}`} /></div>

      {note && <Banner>{note}</Banner>}
      {k.status !== 'approved' && (
        <Card pad>
          <CardTitle title={k.status === 'pending' ? 'Waiting for approval' : 'Rejected'} right={k.status === 'pending' ? <Badge kind="due">Waiting</Badge> : <Badge kind="end">Rejected</Badge>} />
          <Helper icon="info">
            {k.status === 'pending'
              ? `${k.owner?.name ?? 'The owner'} signed up on ${formatDay(k.createdAt.slice(0, 10))} and can’t use Tiffinify until you approve the kitchen. They get an email either way.`
              : `Rejected${k.reviewedAt ? ` on ${formatDay(k.reviewedAt.slice(0, 10))}` : ''}. Approving it now lets them in and emails them.`}
          </Helper>
          <DecideButtons id={k.id} name={k.name} status={k.status} onDone={setNote} />
        </Card>
      )}

      <Hero label="This kitchen">
        <div className="team-stats">
          <span><b>{k.activeSubscriberCount}</b>active of {k.subscriberCount}</span>
          <span><b>{k.todayDeliveries.delivered}</b>delivered today</span>
          <span><b>{formatInr(k.revenueThisMonth)}</b>this month</span>
          <span><b>{formatInr(k.totalRevenue)}</b>all time</span>
        </div>
      </Hero>

      <div className="stack">
        <BtnPrimary busy={visit.isPending} label={visit.isPending ? 'Opening…' : 'Open this kitchen'} onClick={() => visit.mutate(k.id)} />
        <Helper icon="info">You help as staff: everything except adding or removing its people. You are not added to the kitchen.</Helper>
        <ErrorText>{visit.error ? errorMessage(visit.error) : ''}</ErrorText>
      </div>

      <Card pad>
        <CardTitle title="People" right={<Mono>{k.staffCount}</Mono>} />
        <div>
          {k.staff.map((s) => (
            <div className="list-row" key={s.userId}>
              <span className="grow">
                <b>{s.name}</b>
                <small className="ellipsis">{roleLabel(s.role)}{s.email || s.phone ? ` · ${s.email ?? s.phone}` : ''}</small>
              </span>
              <small>{joined(s.joinedAt)}</small>
            </div>
          ))}
        </div>
      </Card>

      <Card pad>
        <CardTitle title="Kitchen rules" />
        <div>
          <KV k="Delivery days" v={dayNames(k.deliveryDays)} />
          <KV k="UPI ID" v={k.upiId ?? 'Not set'} />
          <KV k="Delivery areas" v={d.areas.length === 0 ? 'None' : d.areas.map((a) => a.name).join(', ')} />
        </div>
      </Card>

      {d.plans.length > 0 && (
        <Card pad>
          <CardTitle title="Plans" />
          <div>
            {d.plans.map((p) => (
              <div className="list-row" key={p.id}>
                <span className="grow"><b>{p.name}</b><small>{p.mealDays} meal days · {mealsLabel(p.slot)}</small></span>
                <span className="price">{formatInr(p.price)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card pad>
        <CardTitle title="Customers" right={<Mono>{d.subscribers.length < k.subscriberCount ? `newest ${d.subscribers.length}` : String(k.subscriberCount)}</Mono>} />
        {d.subscribers.length === 0 ? (
          <Empty title="No customers yet" />
        ) : (
          <div>
            {d.subscribers.map((s) => (
              <div className="list-row" key={s.id}>
                <span className="grow">
                  <b>{s.name}</b>
                  <small className="ellipsis">{[s.area, s.activeSubscription ? `${s.activeSubscription.planName} till ${formatShort(s.activeSubscription.endDate)}` : 'No running plan'].filter(Boolean).join(' · ')}</small>
                </span>
                <IconBtn
                  icon="trash"
                  label={`Delete ${s.name}`}
                  ghost
                  disabled={delSub.isPending && delSub.variables === s.id}
                  onClick={async () => {
                    if (await confirm({ title: `Delete ${s.name}?`, body: 'Every plan, delivery and payment of theirs goes too, for good.', yes: 'Delete customer', danger: true })) delSub.mutate(s.id);
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card pad>
        <CardTitle title="Latest payments" />
        {d.recentPayments.length === 0 ? (
          <Empty title="No payments yet" />
        ) : (
          <div>
            {d.recentPayments.map((p) => (
              <div className="list-row" key={p.id}>
                <span className="grow"><b>{p.subscriberName}</b><small>{p.kind === 'refund' ? 'Refund · ' : ''}{formatShort(p.paidOn)} · {p.method.toUpperCase()}</small></span>
                <span className="price">{p.kind === 'refund' ? '−' : ''}{formatInr(p.amount)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card pad className="danger">
        <CardTitle title="Danger zone" />
        <div className="stack">
          <p className="sub">
            Permanently delete <b>{k.name}</b> and all associated subscribers, subscriptions, deliveries, and payment records. This cannot be undone.
          </p>
          <BtnSecondary
            danger
            icon="trash"
            label={delKitchen.isPending ? 'Deleting kitchen…' : 'Delete this kitchen'}
            busy={delKitchen.isPending}
            onClick={async () => {
              if (await confirm({ title: `Delete ${k.name}?`, body: 'Its customers, plans, deliveries, payments and staff go too, for good.', yes: 'Delete kitchen', danger: true })) delKitchen.mutate();
            }}
          />
          <ErrorText>{delKitchen.error ? errorMessage(delKitchen.error) : ''}</ErrorText>
        </div>
      </Card>
    </main>
  );
}

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
function dayNames(days: number[]): string {
  const sorted = [...days].sort();
  const run = sorted.length > 2 && sorted.every((d, i) => i === 0 || d === sorted[i - 1]! + 1);
  return run ? `${DAY_NAMES[sorted[0]! - 1]} to ${DAY_NAMES[sorted[sorted.length - 1]! - 1]}` : sorted.map((d) => DAY_NAMES[d - 1]).join(', ');
}

/** Sets up a kitchen for a new owner: they sign in with that email and find it ready. */
export function AdminNewKitchen() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [upi, setUpi] = useState('');
  const create = useMutation({
    mutationFn: () => {
      const body: CreateAdminKitchenInput = { name, ownerEmail, ownerName: ownerName.trim() || undefined, upiId: upi.trim() || null };
      return post<{ kitchenId: string }>('/admin/kitchens', body);
    },
    onSuccess: async (r) => { await qc.invalidateQueries(); nav(`/admin/k/${r.kitchenId}`, { replace: true }); },
  });
  const fields = create.error instanceof ApiClientError ? create.error.fields : {};

  return (
    <main className="main task">
      <TopBar to="/admin?tab=kitchens" kind="close" label="Close" logo={false} />
      <TaskTitle title="Add a kitchen" sub="For a new owner. They sign in with this email and find it ready." />
      <Card pad>
        <Field label="Kitchen name" id="ak-name" value={name} onChange={setName} placeholder="e.g. Rasoi Ghar" error={fields.name} />
        <Field label="Owner's email" id="ak-email" type="email" value={ownerEmail} onChange={setOwnerEmail} placeholder="owner@example.com" autoComplete="off" error={fields.ownerEmail} />
        <Field label="Owner's name (optional)" id="ak-owner" value={ownerName} onChange={setOwnerName} placeholder="As they like to be called" error={fields.ownerName} />
        <Field label="UPI ID (optional)" id="ak-upi" value={upi} onChange={setUpi} placeholder="kitchen@upi" hint="Added to their payment and renewal messages." error={fields.upiId} />
      </Card>
      <ErrorText>{create.error && Object.keys(fields).length === 0 ? errorMessage(create.error) : ''}</ErrorText>
      <BtnPrimary busy={create.isPending} label={create.isPending ? 'Adding…' : 'Add kitchen'} icon="check" onClick={() => create.mutate()} disabled={create.isPending || name.trim().length < 2 || !ownerEmail.trim()} />
    </main>
  );
}
